import { makeGqlSuspenseQuery } from '@/utilities/gqlQuery';
import { gql, ResultOf, FragmentOf } from '@/utilities/gql';
import { isDemoPortfolio } from '../utils/demoCheck';

export const PORTFOLIO_HOLDINGS_DIVIDEND_FORECAST_FRAGMENT = gql(`
  fragment portfolioHoldingsDividendForecastFields on HoldingDividendForecast @_unmask{
    isAnalysisRestricted
    company {
      id
      name
      canonicalURL
      exchangeCountryISO
      tickerSymbol
      exchangeSymbol
      uniqueSymbol
      score {
        value
        future
        past
        health
        dividend
        sentence
        total
      }
    }
    dividendForecast {
      last1y {
        payment,
        dividendYield,
        yieldOnCapitalInvested
        dividendPaymentsGrowthAnnual,
      }
      next1y {
        payment,
        dividendYield,
        yieldOnCapitalInvested
        dividendPaymentsGrowthAnnual,
      }
      next2y {
        payment,
        dividendYield,
        yieldOnCapitalInvested
        dividendPaymentsGrowthAnnual,
      }
      next3y {
        payment,
        dividendYield,
        yieldOnCapitalInvested
        dividendPaymentsGrowthAnnual,
      }
    }
  }
`);

const QUERY = gql(
  `
  query getPortfolioHoldingsDividendForecast($portfolioId: String!, $isDemo: Boolean!) {
    portfolioHoldingsDividendForecast(id: $portfolioId) @skip(if: $isDemo) {
      ...portfolioHoldingsDividendForecastFields
    }
    samplePortfolioHoldingsDividendForecast(id: $portfolioId) @include(if: $isDemo) {
      ...portfolioHoldingsDividendForecastFields
    }
  }
`,
  [PORTFOLIO_HOLDINGS_DIVIDEND_FORECAST_FRAGMENT]
);

type HookParams = {
  portfolioId: string;
};

export const usePortfolioHoldingsDividendForecastCreator =
  (currencyISO: string) =>
  ({ portfolioId }: HookParams) => {
    // The demo portfolio is a public portfolio that is used to showcase the app.
    // It is not a real user portfolio.
    // This portfolio can be accessed by anyone, which mean we don't check the user's permission to access it.
    // Therefore, it has another dedicated query to access.
    // Currently the demo access is determined by the portfolio feature at route level.
    // However, due to the similarity between the demo and private portfolio,
    // By doing this check, we simplify the way we detect the demo portfolio.

    // TODO: We are planning to remove the samplePortfolio query
    // so that the Portfolio query returns both public and private portfolio.
    // This way we can remove this check and have a single query for all portfolios.
    const isDemo = isDemoPortfolio(portfolioId);

    const { data, ...rest } = makeGqlSuspenseQuery(QUERY, {
      queryKey: ['getPortfolioHoldingsDividendForecast', currencyISO],
    })({
      portfolioId,
      isDemo,
    });

    return {
      data: isDemo
        ? {
            portfolioHoldingsDividendForecast:
              data.samplePortfolioHoldingsDividendForecast,
          }
        : data,
      ...rest,
    };
  };

export type PortfolioDividendForecastData = Omit<
  ResultOf<typeof QUERY>,
  'samplePortfolioHoldingsDividendForecast'
>;

export type PortfolioDividendHolding = FragmentOf<
  typeof PORTFOLIO_HOLDINGS_DIVIDEND_FORECAST_FRAGMENT
>;
